import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Clock3, Printer, RefreshCw, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import { printDocument } from "../lib/printer";

const POLL_INTERVAL = 10000;

function formatTime(value) {
  if (!value) return "-";

  let date;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const isDateTime = /^\d{4}-\d{2}-\d{2}T/.test(value);
    date = new Date(
      isDateTime && !hasTimezone ? `${value}Z` : value
    );
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatExtra(value) {
  if (value == null || value === "") return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(formatExtra).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return String(
      value.name ||
      value.label ||
      value.option_name ||
      value.value ||
      value.title ||
      ""
    );
  }

  return "";
}

function TicketItem({ item }) {
  const variants = formatExtra(item.variants);
  const modifiers = formatExtra(item.modifiers);

  return (
    <div className="border-t border-border/70 py-3 first:border-t-0">
      <div className="flex items-start gap-3">
        <span className="flex h-9 min-w-9 items-center justify-center rounded-lg bg-primary/15 px-2 text-base font-bold text-primary">
          {Number(item.qty || 0)}
        </span>

        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-snug break-words">
            {item.name || "Menu tanpa nama"}
          </p>

          {variants && (
            <p className="mt-1 text-sm text-muted-foreground">
              Varian: {variants}
            </p>
          )}

          {modifiers && (
            <p className="mt-1 text-sm text-muted-foreground">
              Tambahan: {modifiers}
            </p>
          )}

          {item.notes && (
            <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-amber-500/10 px-2.5 py-2 text-sm text-amber-300">
              Catatan: {item.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function TicketCard({ ticket, onDone, onPrint, busy, printBusy }) {
  return (
    <article
      data-testid={`kitchen-ticket-${ticket.id}`}
      className="flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-all text-lg font-bold">
            {ticket.transaction_number || "Pesanan"}
          </p>

          <p className="mt-1 text-sm text-muted-foreground">
            {ticket.customer_name || "Pelanggan"}
            {ticket.batch_sequence != null
              ? ` · Batch ${ticket.batch_sequence}`
              : ""}
          </p>
        </div>

        <span className="shrink-0 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-300">
          OPEN
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock3 size={13} />
          {formatTime(ticket.created_at)}
        </span>
      </div>

      <div className="mt-3 flex-1">
        {(Array.isArray(ticket.items) ? ticket.items : []).map((item, index) => (
          <TicketItem
            key={`${item.product_id || item.name || "item"}-${index}`}
            item={item}
          />
        ))}
      </div>

      <button
        type="button"
        data-testid={`kitchen-ticket-print-${ticket.id}`}
        disabled={printBusy}
        onClick={() => onPrint(ticket, true)}
        className="mb-2 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Printer size={18} />
        {printBusy ? "Mencetak..." : "Cetak Ulang"}
      </button>

      <button
        type="button"
        data-testid={`kitchen-ticket-done-${ticket.id}`}
        disabled={busy}
        onClick={() => onDone(ticket)}
        className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <CheckCircle2 size={18} />
        {busy ? "Memproses..." : "Selesai"}
      </button>
    </article>
  );
}

export default function KitchenDisplayPage() {
  const { session } = useAuth();
  const { outlet } = usePos();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyTicketId, setBusyTicketId] = useState(null);
  const [printingTicketId, setPrintingTicketId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const requestInProgress = useRef(false);
  const refreshPending = useRef(false);
  const mounted = useRef(true);
  const previousTicketIds = useRef(null);
  const printQueueRef = useRef(Promise.resolve());
  const activeOutletIdRef = useRef(outlet?.id);
  activeOutletIdRef.current = outlet?.id;

  const loadTickets = useCallback(async (silent = false) => {
    if (!outlet?.id) return;
    const requestedOutletId = outlet.id;

    if (requestInProgress.current) {
      if (silent) refreshPending.current = true;
      return;
    }

    requestInProgress.current = true;

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await api.get("/kitchen-tickets", {
        params: { status: "OPEN", outlet_id: outlet?.id },
      });

      if (
        !mounted.current ||
        activeOutletIdRef.current !== requestedOutletId
      ) return;

      const data = response.data;
      const ticketList = Array.isArray(data) ? data : data?.items || [];
      setTickets(
        [...ticketList].sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        )
      );
      setLastUpdated(new Date());
    } catch (error) {
      if (mounted.current) {
        toast.error(apiError(error, "Gagal memuat antrean dapur."));
      }
    } finally {
      requestInProgress.current = false;

      if (mounted.current) {
        setLoading(false);
        setRefreshing(false);

        if (refreshPending.current) {
          refreshPending.current = false;
          window.setTimeout(() => {
            if (mounted.current) loadTickets(true);
          }, 0);
        }
      } else {
        refreshPending.current = false;
      }
    }
  }, [outlet?.id]);

  useEffect(() => {
    mounted.current = true;
    previousTicketIds.current = null;
    refreshPending.current = false;
    setTickets([]);
    setLastUpdated(null);

    if (!outlet?.id) {
      setLoading(false);
      setRefreshing(false);
      return () => {
        mounted.current = false;
      };
    }

    loadTickets();

    const interval = window.setInterval(() => {
      if (outlet?.id) loadTickets(true);
    }, POLL_INTERVAL);

    return () => {
      mounted.current = false;
      window.clearInterval(interval);
    };
  }, [loadTickets, outlet?.id]);

  const printKitchenTicket = useCallback((ticket, manual = false) => {
    const requestedOutletId = outlet?.id;
    const run = async () => {
      if (!ticket?.id || !requestedOutletId) return;
      if (activeOutletIdRef.current !== requestedOutletId) return;
      if (ticket.outlet_id !== requestedOutletId) {
        toast.error("Tiket tidak cocok dengan outlet aktif. Cetak dibatalkan.");
        return;
      }
      if (mounted.current) setPrintingTicketId(ticket.id);

      try {
        const tenantId = session?.tenant?.id;
        const outletId = outlet?.id;

        if (!tenantId || !outletId) {
          throw new Error("Tenant atau outlet aktif belum tersedia.");
        }

        const readLocal = (key) => {
          try {
            return JSON.parse(localStorage.getItem(key));
          } catch {
            return null;
          }
        };

        const printer = readLocal("gloo.kitchen_printer");

        if (!printer || !printer.name?.trim()) {
          throw new Error(
            "Printer dapur belum dikonfigurasi. Buka Pengaturan > Hardware > Kitchen Printer."
          );
        }

        if (
          printer.connection !== "cleanter" ||
          printer.adapter !== "cleanter"
        ) {
          throw new Error(
            "Kitchen Printer harus menggunakan koneksi Android — Cleanter."
          );
        }

        const ledgerKey = `gloo.kitchen_print_ledger.v1:${tenantId}:${outletId}`;
        const storedIds = readLocal(ledgerKey);
        const printedIds = Array.isArray(storedIds) ? storedIds : [];

        if (!manual && printedIds.includes(ticket.id)) return;

        if (
          outletId !== requestedOutletId ||
          activeOutletIdRef.current !== requestedOutletId ||
          ticket.outlet_id !== requestedOutletId
        ) {
          return;
        }

        await printDocument({
          document: {
            type: "kitchen_ticket",
            data: ticket,
            version: 1,
          },
          printer: {
            ...printer,
            connection: "cleanter",
            adapter: "cleanter",
          },
          copies: 1,
        });

        localStorage.setItem(
          ledgerKey,
          JSON.stringify([...new Set([...printedIds, ticket.id])].slice(-500))
        );

        toast.success(
          manual ? "Tiket dapur dikirim ulang." : "Tiket dapur dikirim ke Cleanter."
        );
      } catch (error) {
        toast.error(apiError(error, "Gagal mencetak tiket dapur."));
      } finally {
        if (mounted.current) setPrintingTicketId(null);
      }
    };

    const queued = printQueueRef.current.then(run, run);
    printQueueRef.current = queued.catch(() => {});
    return queued;
  }, [session?.tenant?.id, outlet?.id]);

  useEffect(() => {
    if (!lastUpdated) return;

    const currentIds = new Set(
      tickets.map((ticket) => ticket?.id).filter(Boolean)
    );

    if (previousTicketIds.current === null) {
      previousTicketIds.current = currentIds;
      return;
    }

    const newTickets = tickets.filter(
      (ticket) =>
        ticket?.id &&
        ticket.outlet_id === outlet?.id &&
        !previousTicketIds.current.has(ticket.id)
    );
    previousTicketIds.current = currentIds;

    if (newTickets.length) {
      void (async () => {
        for (const ticket of newTickets) {
          await printKitchenTicket(ticket, false);
        }
      })();
    }
  }, [tickets, lastUpdated, printKitchenTicket, outlet?.id]);

  const markDone = async (ticket) => {
    if (!ticket?.id || busyTicketId) return;

    setBusyTicketId(ticket.id);

    try {
      await api.post(`/kitchen-tickets/${encodeURIComponent(ticket.id)}/done`, {});

      if (!mounted.current) return;

      setTickets((current) => current.filter((item) => item.id !== ticket.id));
      toast.success("Tiket ditandai selesai.");
      setLastUpdated(new Date());

      loadTickets(true);
    } catch (error) {
      toast.error(apiError(error, "Gagal menyelesaikan tiket. Silakan coba lagi."));
      loadTickets(true);
    } finally {
      if (mounted.current) setBusyTicketId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="kitchen-display-page">
      <PageHeader
        title="Kitchen Display"
        subtitle="Antrean pesanan dapur yang sedang diproses."
        testid="kitchen-display-header"
      >
        <button
          type="button"
          onClick={() => loadTickets(true)}
          disabled={refreshing}
          className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60"
        >
          <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          Perbarui
        </button>
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
        <span>
          {tickets.length} tiket menunggu
        </span>
        <span>
          {lastUpdated
            ? `Pembaruan terakhir ${formatTime(lastUpdated)}`
            : "Menunggu data"}
          {" · "}Otomatis setiap 10 detik
        </span>
      </div>

      {loading ? (
        <div className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card text-muted-foreground">
          <RefreshCw size={24} className="animate-spin" />
          <p>Memuat antrean dapur...</p>
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-5 text-center">
          <UtensilsCrossed size={36} className="mb-3 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Belum ada pesanan</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tiket baru akan muncul otomatis saat tersedia.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              onDone={markDone}
              onPrint={printKitchenTicket}
              printBusy={printingTicketId === ticket.id}
              busy={busyTicketId === ticket.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
